import React from "react";
import CodeBlock from "@/components/CodeBlock";
import { Card } from "@/components/ui/card";
import { FileCode } from "lucide-react";

const CommonDoc = () => {
  return (
    <section className="mb-12">
      {/* Header */}
      <h2 className="text-3xl font-bold text-white mb-6">
        <FileCode className="inline h-8 w-8 mr-2 text-emerald-400" />
        AxisNet.Common Library
      </h2>

      {/* Intro */}
      <p className="text-slate-300 mb-6">
        <strong>AxisNet.Common</strong> is the shared utility layer of AxisNet.
        It contains cross-cutting concerns such as filtering, sorting,
        pagination, request models, and exception structures. These components
        are used across all layers — Domain, Application, Infrastructure, and
        CRUD — to ensure consistency, developer productivity, and clean
        architectural boundaries.
      </p>

      <p className="text-slate-300 mb-6">
        This library removes the need to manually implement repetitive logic and
        provides standardized, reusable primitives that improve maintainability
        and reduce boilerplate code.
      </p>

      {/* What It Provides */}
      <h3 className="text-2xl font-semibold text-white mb-4 mt-10">
        What AxisNet.Common Provides
      </h3>

      <div className="not-prose mb-6">
        <Card className="p-6 bg-slate-900/60 border-slate-800">
          <ul className="space-y-3 text-slate-300">
            <li>
              <strong className="text-emerald-400">Filtering Tools</strong> –
              Build dynamic LINQ queries using field/value/operators.
            </li>
            <li>
              <strong className="text-emerald-400">Sorting</strong> – Apply
              ASC/DESC sorting to queries.
            </li>
            <li>
              <strong className="text-emerald-400">Pagination Utilities</strong>{" "}
              – Standard paging response models and IQueryable extensions.
            </li>
            <li>
              <strong className="text-emerald-400">Request Models</strong> –
              FilterRequest, PageRequest, PaginateResponse for API endpoints.
            </li>
            <li>
              <strong className="text-emerald-400">Safe Exceptions</strong> –
              Base exception type for predictable, controlled error responses.
            </li>
          </ul>
        </Card>
      </div>

      {/* ------------------------------ */}
      {/* FILTERING */}
      {/* ------------------------------ */}

      <h3 className="text-2xl font-semibold text-white mb-6 mt-12">
        Filtering
      </h3>

      <p className="text-slate-300 mb-4">
        AxisNet provides a lightweight but flexible filtering model that allows
        API requests to dynamically build backend filters without manually
        writing conditional logic. These filters are consumed inside
        repositories using <code>QueryFilter.ApplyFilter</code>.
      </p>

      {/* Filter */}
      <h4 className="text-xl font-semibold text-white mb-3">Filter</h4>

      <p className="text-slate-300 mb-4">
        Represents a single filter rule based on field, value, and operator.
      </p>

      <div className="not-prose mb-4">
        <CodeBlock
          language="csharp"
          code={`public class Filter
{
    public Sort? Sort { get; set; }
    public string? Value { get; set; }
    public string? Field { get; set; }
    public FilterOperator? Operator { get; set; } = FilterOperator.Equals;
}`}
        />
      </div>

      {/* FilterOperator */}
      <h4 className="text-xl font-semibold text-white mb-3">FilterOperator</h4>

      <p className="text-slate-300 mb-4">
        Defines supported comparison operations.
      </p>

      <div className="not-prose mb-4">
        <CodeBlock
          language="csharp"
          code={`public enum FilterOperator
{
    Equals,
    NotEquals,
    GreaterThan,
    LessThan,
    Contains
}`}
        />
      </div>

      {/* Sort */}
      <h4 className="text-xl font-semibold text-white mb-3">Sort</h4>

      <p className="text-slate-300 mb-4">
        Specifies the property and direction (ASC/DESC) for sorting queries.
      </p>

      <div className="not-prose mb-6">
        <CodeBlock
          language="csharp"
          code={`public class Sort
{
    public string? PropertyName { get; set; }
    public string? Direction { get; set; } = "DESC";
}`}
        />
      </div>

      {/* Usage Example */}
      <h4 className="text-xl font-semibold text-white mb-4 mt-8">
        Example – Applying a Dynamic Filter
      </h4>

      <div className="not-prose mb-6">
        <CodeBlock
          language="csharp"
          code={`var filter = new Filter
{
    Field = "Name",
    Value = "murat",
    Operator = FilterOperator.Contains,
    Sort = new Sort { PropertyName = "CreatedAt", Direction = "DESC" }
};

var users = await _userRepository.GetAsync(
    page: 1,
    size: 10,
    filter: filter
);`}
        />
      </div>

      {/* ------------------------------ */}
      {/* PAGING */}
      {/* ------------------------------ */}

      <h3 className="text-2xl font-semibold text-white mb-8 mt-12">
        Pagination
      </h3>

      <p className="text-slate-300 mb-4">
        AxisNet provides a complete paging ecosystem, including request models,
        response containers, and queryable extensions for server-side paging.
      </p>

      {/* BasePageableModel */}
      <h4 className="text-xl font-semibold text-white mb-3">
        BasePageableModel
      </h4>

      <p className="text-slate-300 mb-4">
        Base metadata container used for paged API responses.
      </p>

      <div className="not-prose mb-4">
        <CodeBlock
          language="csharp"
          code={`public class BasePageableModel
{
    public int Page { get; set; }
    public int Size { get; set; }
    public int Count { get; set; }
    public bool HasNext { get; set; }
    public bool HasPrev { get; set; }
    public int TotalPages { get; set; }
}`}
        />
      </div>

      {/* Paginate<TEntity> */}
      <h4 className="text-xl font-semibold text-white mb-3">
        Paginate&lt;TEntity&gt;
      </h4>

      <p className="text-slate-300 mb-4">
        This structure is returned internally from repositories via{" "}
        <code>ToPaginateAsync()</code>.
      </p>

      <div className="not-prose mb-4">
        <CodeBlock
          language="csharp"
          code={`public class Paginate<TEntity>
{
    public int Page { get; set; }
    public int Size { get; set; }
    public int Count { get; set; }
    public int TotalPages { get; set; }
    public IEnumerable<TEntity>? Items { get; set; }

    public bool HasNext => Page < TotalPages;
    public bool HasPrev => Page > 1;
}`}
        />
      </div>

      {/* QueryablePaginateExtensions */}
      <h4 className="text-xl font-semibold text-white mb-3">
        QueryablePaginateExtensions
      </h4>

      <p className="text-slate-300 mb-4">
        Converts an <code>IQueryable</code> into a <code>Paginate</code>{" "}
        structure with a single method call.
      </p>

      <div className="not-prose mb-6">
        <CodeBlock
          language="csharp"
          code={`var result = await query.ToPaginateAsync(page: 1, size: 20);`}
        />
      </div>

      {/* ------------------------------ */}
      {/* PARAM REQUESTS */}
      {/* ------------------------------ */}

      <h3 className="text-2xl font-semibold text-white mb-8 mt-12">
        Request Models (Params)
      </h3>

      <p className="text-slate-300 mb-4">
        Request DTOs used by API endpoints for filtering and paging. These map
        directly from incoming HTTP query parameters.
      </p>

      {/* FilterRequest */}
      <h4 className="text-xl font-semibold text-white mb-3">FilterRequest</h4>

      <div className="not-prose mb-4">
        <CodeBlock
          language="csharp"
          code={`public class FilterRequest
{
    public Sort? Sort { get; set; }
    public string? Value { get; set; }
    public string? Field { get; set; }
    public FilterOperator? Operator { get; set; } = FilterOperator.Equals;
}`}
        />
      </div>

      {/* PageRequest */}
      <h4 className="text-xl font-semibold text-white mb-3">PageRequest</h4>

      <div className="not-prose mb-4">
        <CodeBlock
          language="csharp"
          code={`public class PageRequest
{
    public int Page { get; set; }
    public int Size { get; set; }
}`}
        />
      </div>

      {/* Example – API Usage */}
      <h4 className="text-xl font-semibold text-white mb-4 mt-8">
        Example – Using FilterRequest + PageRequest in Controller
      </h4>

      <div className="not-prose mb-6">
        <CodeBlock
          language="csharp"
          code={`[HttpGet]
public async Task<IActionResult> GetUsers([FromQuery] FilterRequest filter, [FromQuery] PageRequest page)
{
    var result = await _userRepository.GetAsync(
        page: page.Page,
        size: page.Size,
        filter: new Filter
        {
            Field = filter.Field,
            Value = filter.Value,
            Operator = filter.Operator,
            Sort = filter.Sort
        }
    );

    return Ok(result);
}`}
        />
      </div>

      {/* PaginateResponse */}
      <h4 className="text-xl font-semibold text-white mb-3">
        PaginateResponse&lt;T&gt;
      </h4>

      <p className="text-slate-300 mb-4">
        DTO wrapper used to return paginated results to the API client.
      </p>

      <div className="not-prose mb-6">
        <CodeBlock
          language="csharp"
          code={`public class PaginateResponse<T> : BasePageableModel
{
    public IEnumerable<T> Items { get; set; } = new List<T>();
}`}
        />
      </div>

      {/* ------------------------------ */}
      {/* EXCEPTIONS */}
      {/* ------------------------------ */}

      <h3 className="text-2xl font-semibold text-white mb-8 mt-12">
        Safe Exceptions
      </h3>

      <p className="text-slate-300 mb-4">
        AxisNet introduces <code>AxisSafeException</code> to provide a secure,
        consistent way of throwing controlled exceptions. This prevents leaking
        internal stack traces while still offering rich, meaningful error
        information.
      </p>

      <h4 className="text-xl font-semibold text-white mb-3">
        AxisSafeException
      </h4>

      <div className="not-prose mb-4">
        <CodeBlock
          language="csharp"
          code={`public abstract class AxisSafeException : Exception
{
    protected AxisSafeException() : base() { }
    protected AxisSafeException(string message) : base(message) { }
    protected AxisSafeException(string message, Exception? innerException)
        : base(message, innerException) { }
}`}
        />
      </div>

      {/* Example Usage */}
      <h4 className="text-xl font-semibold text-white mb-4 mt-8">
        Example – Throwing a Safe Exception
      </h4>

      <div className="not-prose mb-12">
        <CodeBlock
          language="csharp"
          code={`public class UserNotFoundException : AxisSafeException
{
    public UserNotFoundException(Guid userId)
        : base($"User with ID {userId} not found.") {}
}`}
        />
      </div>
    </section>
  );
};

export default CommonDoc;
